import { useEffect, useMemo, useRef, type KeyboardEvent } from 'react';
import { Badge, Button, Skeleton, Text } from '@repletoryreach/sdk-ui';
import { ScrollArea } from '@repletoryreach/sdk-ui/scroll';
import { Alert, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import type { ConversationSummary } from '@repletoryreach/sdk-api/inbox';
import { whatsAppPreview } from '@repletoryreach/sdk-utils/whatsapp-text';
import { useT } from '../../../i18n/I18nProvider';
import { formatRelative, formatCount } from '../../../lib/format';
import { SlaBadge } from './SlaBadge';
import { ContactAvatar } from './ContactAvatar';
import { ChannelMark } from './ChannelMark';

export interface ConversationListProps {
  items: ConversationSummary[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  loading: boolean;
  error: unknown;
  now: number;
  /** Present when the server says there is another page. */
  onLoadMore?: () => void;
  loadingMore?: boolean;
  /** Total across every page, from `meta.total`. */
  total?: number;
  /** True when a filter or search term is narrowing the list. */
  filtered: boolean;
  onClearFilters: () => void;
  onAttachNumber: () => void;
}

/** The state pill on a row. `pending` is the state the service writes while a reply is owed. */
const ROW_STATES = new Set(['open', 'pending', 'resolved', 'archived']);

/**
 * The conversation queue.
 *
 * Rows follow `_UI_improvements/tenant/images/inbox.png`: an avatar carrying
 * the channel badge, the name and the relative time on one line, the preview
 * on the next, and the state and unread count on a third. Selection is a left
 * accent bar plus a tint, so it survives a greyscale screen.
 *
 * The accessibility work from the earlier rebuild is unchanged: this is a real
 * `listbox`/`option` with a roving tabindex and arrow, Home and End keys, the
 * error state is distinct from the empty state, loading is skeleton rows
 * shaped like rows, and the unread badge announces a sentence rather than a
 * bare number.
 */
export function ConversationList({
  items, selectedId, onSelect, loading, error, now,
  onLoadMore, loadingMore, total, filtered, onClearFilters, onAttachNumber
}: ConversationListProps) {
  const t = useT();
  const listRef = useRef<HTMLDivElement>(null);

  const selectedIndex = useMemo(
    () => items.findIndex((c) => c.id === selectedId),
    [items, selectedId]
  );

  // Keep the selected row in view when selection moves from outside the list
  // (a deep link, or the keyboard).
  useEffect(() => {
    if (selectedIndex < 0) return;
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${selectedIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (items.length === 0) return;
    const move = (next: number) => {
      e.preventDefault();
      const clamped = Math.max(0, Math.min(items.length - 1, next));
      const target = items[clamped];
      if (target) onSelect(target.id);
    };
    const from = selectedIndex < 0 ? 0 : selectedIndex;
    if (e.key === 'ArrowDown' || e.key === 'j') move(from + 1);
    else if (e.key === 'ArrowUp' || e.key === 'k') move(from - 1);
    else if (e.key === 'Home') move(0);
    else if (e.key === 'End') move(items.length - 1);
  }

  if (error) {
    return (
      <div className="rr-inbox2__list-state">
        <Alert color="error" role="alert">{t('inbox.loadError')}</Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="rr-inbox2__list-state" aria-busy="true" aria-label={t('common.loading')}>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="rr-inbox2__row-skeleton">
            <Skeleton height={36} width={36} radius="xl" />
            <div className="rr-inbox2__row-skeleton-lines">
              <Skeleton height={12} width="55%" />
              <Skeleton height={10} width="85%" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="rr-inbox2__list-state">
        <EmptyState
          title={filtered ? t('inbox.empty.filtered') : t('inbox.empty')}
          description={filtered ? t('inbox.empty.filtered.hint') : t('inbox.empty.hint')}
          action={
            filtered
              ? <Button size="xs" variant="default" onClick={onClearFilters}>{t('inbox.filters.clear')}</Button>
              : <Button size="xs" onClick={onAttachNumber}>{t('inbox.empty.action')}</Button>
          }
        />
      </div>
    );
  }

  return (
    <>
      <ScrollArea
        data-guide="inbox-list"
        className="rr-inbox2__rows"
        axis="y"
        overscroll="contain"
        focusable={false}
        viewportRef={listRef}
        viewportProps={{ role: 'listbox', 'aria-label': t('inbox.list.label'), tabIndex: 0, onKeyDown }}
      >
        {items.map((c, i) => {
          const selected = c.id === selectedId;
          // On Messenger and Instagram there is no telephone number to fall
          // back to, so the fallback is the channel's own id for the person.
          const name = c.contact_display_name ?? c.contact_msisdn ?? c.contact_ref;
          const state = ROW_STATES.has(c.state_code ?? '') ? c.state_code! : null;
          return (
            <div
              key={c.id}
              data-index={i}
              // The guide opens the first conversation to show the thread (8 October 2026).
              data-guide="inbox-row"
              role="option"
              aria-selected={selected}
              tabIndex={-1}
              className={`rr-inbox2__row${selected ? ' is-selected' : ''}${c.unread_count > 0 ? ' is-unread' : ''}`}
              onClick={() => onSelect(c.id)}
            >
              {/* The person, and where they are writing from. */}
              <span className="rr-inbox2__avatar-wrap">
                <ContactAvatar name={name} size={36} />
                <ChannelMark channel={c.channel} />
              </span>

              <div className="rr-inbox2__row-main">
                <div className="rr-inbox2__row-top">
                  <span className="rr-inbox2__row-name">{name}</span>
                  <span className="rr-inbox2__row-time">{formatRelative(c.last_message_at)}</span>
                </div>
                <div className="rr-inbox2__row-bottom">
                  {/*
                    Plain text on purpose. The preview is one clipped line, and
                    half a bold phrase cut off by an ellipsis reads as a fault;
                    so does a row that says "*Continue setting up your
                    account*". The markers come off, the words stay.
                  */}
                  <span className="rr-inbox2__row-preview">
                    {c.last_message_preview === '[unsupported]'
                      ? t('inbox.message.unsupported.preview')
                      : whatsAppPreview(c.last_message_preview, 140) || t('inbox.noMessages')}
                  </span>
                  <span className="rr-inbox2__row-badges">
                    <SlaBadge sla={c.sla} now={now} compact />
                    {state && (
                      <span className={`rr-inbox2__state rr-inbox2__state--${state}`}>
                        {t(`inbox.state.${state}`)}
                      </span>
                    )}
                    {c.unread_count > 0 && (
                      <Badge size="xs" color="blue" variant="filled" className="rr-inbox2__unread">
                        <span aria-hidden>{c.unread_count}</span>
                        <span className="rr-visually-hidden">
                          {t('inbox.unread.count', { count: c.unread_count })}
                        </span>
                      </Badge>
                    )}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </ScrollArea>

      {/* The reference states the shown-of-total plainly, which also tells an
          agent whether the queue they are looking at is the whole queue. */}
      <div className="rr-inbox2__list-foot">
        {total !== undefined && (
          <Text role="caption" c="var(--rr-color-text-muted)">
            {t('inbox.showingOf', { shown: formatCount(items.length), total: formatCount(total) })}
          </Text>
        )}
        {onLoadMore && (
          <button type="button" className="rr-inbox2__load-more" onClick={onLoadMore} disabled={loadingMore}>
            {loadingMore ? t('common.loading') : t('inbox.loadMore')}
            <span aria-hidden> ▾</span>
          </button>
        )}
      </div>
    </>
  );
}
